'use client'

import React from 'react'

import { Box, HTMLChakraProps } from '@chakra-ui/react'

import { tokenVar } from '../../theme/tokenVar'
import { Button } from '../Button/Button'
import { IncidentPriorityIcon } from '../Icon/IncidentPriorityIcon'
import { AiOperatorIcon } from '../Icon/SideBar/AiOperatorIcon'
import { StatusPill } from '../Pill/StatusPill'
import type { StatusPillVariant } from '../Pill/StatusPill'
import { PriorityDropdown } from '../PriorityDropdown/PriorityDropdown'
import {
  ColorScheme,
  IncidentCardPriority,
  IncidentCardStatus,
  incidentCardDescriptionChipStyles,
  incidentCardDescriptionChipTextStyles,
  incidentCardDescriptionRowStyles,
  incidentCardDescriptionTextStyles,
  incidentCardDetectionIconStyles,
  incidentCardHeadingRowStyles,
  incidentCardHeadingSideStyles,
  incidentCardStyles,
  incidentCardTimestampStyles,
  incidentCardTitleStyles,
} from './IncidentCard.styles'

export type { ColorScheme, IncidentCardPriority, IncidentCardStatus }

const STATUS_LABELS: Record<IncidentCardStatus, string> = {
  active: 'Active',
  'in-progress': 'In Progress',
  resolved: 'Resolved',
}

const STATUS_PILL_VARIANT: Record<IncidentCardStatus, StatusPillVariant> = {
  active: 'incomplete',
  'in-progress': 'in-progress',
  resolved: 'complete',
}

// CSS variable names for status text colors — used to tint the AiOperatorIcon.
const STATUS_TEXT_VARS: Record<
  ColorScheme,
  Record<IncidentCardStatus, string>
> = {
  dark: {
    active: tokenVar('colors.status.active-text'),
    'in-progress': tokenVar('colors.status.in-progress-text'),
    resolved: tokenVar('colors.status.resolved-text'),
  },
  light: {
    active: tokenVar('colors.light.status.active-text'),
    'in-progress': tokenVar('colors.light.status.in-progress-text'),
    resolved: tokenVar('colors.light.status.resolved-text'),
  },
}

// ─── Incident Status Chip ──────────────────────────────────────────────────────

type IncidentStatusChipProps = {
  status: IncidentCardStatus
  /**
   * When true, shows the HiveWatch AI Operator logo on the chip.
   * Only set to true if the AI Operator feature handled this incident.
   * NEVER substitute another icon.
   */
  aiOperatorUsed?: boolean
  colorScheme?: ColorScheme
}

const IncidentStatusChip = ({
  status,
  aiOperatorUsed = false,
  colorScheme = 'dark',
}: IncidentStatusChipProps) => (
  <StatusPill
    colorScheme={colorScheme}
    fontFamily="'Soehne Kraftig', sans-serif"
    fontSize='14px'
    fontWeight='500'
    gap='8px'
    h='26px'
    icon={
      aiOperatorUsed ? (
        <Box flexShrink={0} h='17.78px' overflow='hidden' w='16px'>
          <AiOperatorIcon strokeColor={STATUS_TEXT_VARS[colorScheme][status]} />
        </Box>
      ) : undefined
    }
    label={STATUS_LABELS[status]}
    lineHeight='20px'
    px='10px'
    py='3px'
    variant={STATUS_PILL_VARIANT[status]}
  />
)

// ─── IncidentCard ──────────────────────────────────────────────────────────────

export type IncidentCardProps = HTMLChakraProps<'div'> & {
  colorScheme?: ColorScheme
  /** Icon representing the detection type (e.g., DoorIcon, CameraIcon) */
  detectionTypeIcon?: React.ReactNode
  /** Incident name / event type */
  title: string
  /** Incident priority level */
  priority?: IncidentCardPriority
  /** Incident operational status */
  status: IncidentCardStatus
  /**
   * When true, shows the HiveWatch AI Operator logo on the status chip.
   * Only set to true if the AI Operator feature was used on this incident.
   */
  aiOperatorUsed?: boolean
  /** Formatted timestamp string */
  timestamp?: string
  /**
   * Context / description row — rendered below the timestamp.
   * Use IncidentCardDescriptionText and IncidentCardDescriptionChip helpers
   * to compose the standard formatted content.
   */
  description?: React.ReactNode
  /** Additional content rendered below the description row (e.g., quick report details) */
  children?: React.ReactNode
  /** When provided, renders a "View trace logs" tertiary button */
  onViewTraceLogs?: () => void
  /** When provided, renders the priority icon as a dropdown that calls back with the new priority */
  onPriorityChange?: (priority: IncidentCardPriority) => void
}

export const IncidentCard = ({
  colorScheme = 'dark',
  detectionTypeIcon,
  title,
  priority,
  status,
  aiOperatorUsed = false,
  timestamp,
  description,
  children,
  onViewTraceLogs,
  onPriorityChange,
  ...rest
}: IncidentCardProps) => (
  <Box {...incidentCardStyles(colorScheme)} {...rest}>
    {/* Heading row */}
    <Box
      {...incidentCardHeadingRowStyles}
      {...(onPriorityChange && { overflow: 'visible' })}
    >
      <Box {...incidentCardHeadingSideStyles} flexShrink={1} minWidth={0}>
        {detectionTypeIcon && (
          <Box {...incidentCardDetectionIconStyles(colorScheme)}>
            {detectionTypeIcon}
          </Box>
        )}
        <Box as='p' {...incidentCardTitleStyles(colorScheme)}>
          {title}
        </Box>
      </Box>
      <Box
        {...incidentCardHeadingSideStyles}
        {...(onPriorityChange && { overflow: 'visible', position: 'relative' })}
      >
        {priority !== undefined &&
          (onPriorityChange ? (
            <PriorityDropdown
              colorScheme={colorScheme}
              onPriorityChange={onPriorityChange}
              priority={priority}
            />
          ) : (
            <IncidentPriorityIcon
              colorScheme={colorScheme}
              priority={priority}
              size={20}
            />
          ))}
        <IncidentStatusChip
          aiOperatorUsed={aiOperatorUsed}
          colorScheme={colorScheme}
          status={status}
        />
      </Box>
    </Box>

    {/* Timestamp */}
    {timestamp && (
      <Box as='p' {...incidentCardTimestampStyles(colorScheme)}>
        {timestamp}
      </Box>
    )}

    {/* Description row */}
    {description && (
      <Box {...incidentCardDescriptionRowStyles}>{description}</Box>
    )}

    {/* Additional content (e.g., quick report details) */}
    {children}

    {/* View trace logs button */}
    {onViewTraceLogs && (
      <Box flexShrink={0}>
        <Button
          color={
            colorScheme === 'light' ? 'light.text.primary' : 'text.primary'
          }
          colorScheme={colorScheme}
          onClick={onViewTraceLogs}
          size='sm'
          variant='tertiary'
        >
          View trace logs
        </Button>
      </Box>
    )}
  </Box>
)

// ─── Description helpers ───────────────────────────────────────────────────────

export type IncidentCardDescriptionTextProps = HTMLChakraProps<'p'> & {
  colorScheme?: ColorScheme
}

/** Plain text fragment for use inside the IncidentCard description prop. */
export const IncidentCardDescriptionText = ({
  colorScheme = 'dark',
  children,
  ...rest
}: IncidentCardDescriptionTextProps) => (
  <Box as='p' {...incidentCardDescriptionTextStyles(colorScheme)} {...rest}>
    {children}
  </Box>
)

export type IncidentCardDescriptionChipProps = {
  colorScheme?: ColorScheme
  children: React.ReactNode
  onClick?: () => void
}

/** Inverted chip for highlighting entity references in the IncidentCard description. */
export const IncidentCardDescriptionChip = ({
  colorScheme = 'dark',
  children,
  onClick,
}: IncidentCardDescriptionChipProps) => (
  <Box
    {...incidentCardDescriptionChipStyles(colorScheme)}
    {...(onClick && {
      cursor: 'pointer',
      onClick,
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      },
      role: 'button',
      tabIndex: 0,
    })}
  >
    <Box as='p' {...incidentCardDescriptionChipTextStyles(colorScheme)}>
      {children}
    </Box>
  </Box>
)
